<script lang="ts">
	//Hooks
	import { createEventDispatcher } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Modal, toast, Input, Chip, Alert, Button } from '@Shelly/shelly-design-system';
	//Types
	import type { LibraryScript, ManifestScriptEntry } from 'src/types/requests/settings/scripts';
	//App Components
	import NoSearchResults from 'src/components/Lists/NoSearchResults.svelte';
	import SearchableBulletedList from './SearchableBulletedList.svelte';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { retryWithBackoff } from 'src/helpers/retry_helpers';
	//Constants
	import {
		MANIFEST_URL,
		MANIFEST_FALLBACK_URL,
		MANIFEST_CACHE_KEY,
		REPO_RAW_BASE,
		GITHUB_REPO_BASE,
	} from '../scripts_constants';

	export let showModal = false;

	let scriptsList: LibraryScript[] = null,
		insertingScriptId: string | null = null,
		libraryLoadError = '',
		isStaleCache = false,
		searchQuery = '',
		selectedTags: Set<string> = new Set();

	const dispatch = createEventDispatcher();

	$: availableTags = [...new Set(scriptsList?.flatMap((s) => s.tags ?? []) ?? [])].sort();

	$: filteredScriptsList =
		scriptsList?.filter((script) => {
			const q = searchQuery.trim().toLowerCase();
			const matchesSearch =
				!q ||
				script.title.toLowerCase().includes(q) ||
				script.description.toLowerCase().includes(q);
			const matchesTags =
				selectedTags.size === 0 || (script.tags ?? []).some((t) => selectedTags.has(t));
			return matchesSearch && matchesTags;
		}) ?? [];

	$: hasActiveFilters = !!searchQuery.trim() || selectedTags.size > 0;
	$: showEmptyResults = !!scriptsList && hasActiveFilters && filteredScriptsList.length === 0;
	$: emptyStateSearch = searchQuery.trim() || [...selectedTags].sort().join(', ');

	$: libraryListItems = filteredScriptsList.map((script, index) => ({
		id: script.url,
		title: script.title,
		description: script.description,
		sourceHref: `${GITHUB_REPO_BASE}${script.url}`,
		sourceLinkText: $format('link_view_script_source', { default: 'View source' }),
		linkText: $format('insert_code', { default: 'Insert code' }),
		icon: 'fas fa-plus',
		loading: insertingScriptId === index.toString(),
		disabled: insertingScriptId !== null && insertingScriptId !== index.toString(),
	}));

	const showErrorToast = () => {
		toast.open({
			type: 'danger',
			title: $format('error_global', {
				default: 'Something went wrong!',
			}),
		});
	};

	const getContent = async (url: string) => {
		try {
			const req = await fetch(url);
			return await req.text();
		} catch {
			showErrorToast();
		}
		return null;
	};

	const mapManifestEntry = (entry: ManifestScriptEntry): LibraryScript => ({
		url: entry.fname,
		title: entry.title,
		description: entry.description ?? '',
		tags: Array.isArray(entry.tags) ? entry.tags : [],
		showDescription: false,
	});

	const fetchManifest = async (url: string): Promise<ManifestScriptEntry[] | null> => {
		const response = await fetch(url);
		if (!response.ok) return null;
		const data = await response.json();
		return Array.isArray(data) ? (data as ManifestScriptEntry[]) : null;
	};

	const readCachedManifest = (): ManifestScriptEntry[] | null => {
		try {
			const raw = localStorage.getItem(MANIFEST_CACHE_KEY);
			if (!raw) return null;
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? (parsed as ManifestScriptEntry[]) : null;
		} catch {
			return null;
		}
	};

	const writeCachedManifest = (data: ManifestScriptEntry[]) => {
		try {
			localStorage.setItem(MANIFEST_CACHE_KEY, JSON.stringify(data));
		} catch {
			// Ignore quota / privacy-mode failures; cache is best-effort.
		}
	};

	const getLibrary = async () => {
		libraryLoadError = '';
		isStaleCache = false;

		// Try primary (GitHub Raw) with a couple of retries, then GitLab fallback.
		const sources = [MANIFEST_URL, MANIFEST_FALLBACK_URL];
		for (const source of sources) {
			try {
				const data = await retryWithBackoff(() => fetchManifest(source), {
					maxAttempts: 2,
					initialDelayMs: 500,
					shouldRetry: (result) => result === null,
				});
				if (data) {
					scriptsList = data.map(mapManifestEntry);
					writeCachedManifest(data);
					return;
				}
			} catch {
				// Try next source
			}
		}

		// All network sources failed; fall back to cached manifest if we have one.
		const cached = readCachedManifest();
		if (cached) {
			scriptsList = cached.map(mapManifestEntry);
			isStaleCache = true;
			return;
		}

		libraryLoadError = $format('error_load_scripts_library', {
			default: "Couldn't load the scripts library. Please try again.",
		});
	};

	const insertCode = async (scriptUrl: string, index: string) => {
		if (insertingScriptId) return;
		insertingScriptId = index;
		try {
			const content = await getContent(`${REPO_RAW_BASE}/${scriptUrl}`);
			if (content) {
				resetFilters();
				showModal = false;
				dispatch('insertScript', content);
			}
		} finally {
			insertingScriptId = null;
		}
	};

	const toggleTag = (tag: string) => {
		if (selectedTags.has(tag)) {
			selectedTags.delete(tag);
		} else {
			selectedTags.add(tag);
		}
		selectedTags = selectedTags;
	};

	const resetFilters = () => {
		searchQuery = '';
		selectedTags = new Set();
	};

	const closeModal = () => {
		resetFilters();
		showModal = false;
	};

	getLibrary();
</script>

{#if showModal}
	<Modal
		bind:showModal
		backdropClose={true}
		size="lg"
		removeScrollPadding
		preserveMaxHeight
		customContent
		titleId="modal-script-library-title-id"
		title={$format('modal_title_script_library', {
			default: 'Library',
		})}
		aria-labelledby="modal-script-library-title"
		on:close={closeModal}
	>
		<svelte:fragment slot="content">
			{#if libraryLoadError}
				<div class="d-flex flex-column padding-l padding-sm-m">
					<Alert type="danger" heading={libraryLoadError} class="mb-s" />
					<Button size="m" width="full" variant="primary" on:click={getLibrary}>
						{$format('button_rescan', { default: 'Rescan' })}
					</Button>
				</div>
			{:else if scriptsList}
				<div class="library-layout">
					<div class="library-filter-bar d-flex flex-column gap-s padding-l padding-sm-m">
						{#if isStaleCache}
							<Alert
								type="warning"
								heading={$format('alert_scripts_library_cached', {
									default:
										"Showing a cached library. Couldn't reach the online source — some scripts may be out of date.",
								})}
								class="mb-xs"
							/>
						{/if}
						<Input
							type="text"
							label={capFirstChar(
								$format('placeholder_search_script_library', {
									default: 'Search scripts...',
								}),
							)}
							icon="fas fa-search"
							clearButton
							autofocus
							class="width-lg-l width-sm-full width-md-l;"
							bind:value={searchQuery}
							id="input-script-library-search"
							aria-labelledby="input-script-library-search"
							a11yProps={{
								iconButtonAriaLabel: $format('input_search_with_clear_button', {
									default: 'Input search with clear button',
								}),
							}}
						/>
						{#if availableTags.length > 0}
							<div class="library-tag-chips mt-s">
								{#each availableTags as tag (tag)}
									<button
										type="button"
										class="library-tag-chip-btn"
										aria-pressed={selectedTags.has(tag)}
										aria-label={selectedTags.has(tag)
											? $format('label_remove_tag_filter', {
													default: 'Remove {tag} filter',
													values: { tag },
												})
											: $format('label_filter_by_tag', {
													default: 'Filter by {tag}',
													values: { tag },
												})}
										on:click={() => toggleTag(tag)}
									>
										<Chip
											variant={selectedTags.has(tag) ? 'primary' : 'secondary'}
											class="mr-xs mb-xs"
											aria-label={tag}
										>
											{tag}
										</Chip>
									</button>
								{/each}
							</div>
						{/if}
					</div>
					<div class="library-list">
						{#if showEmptyResults}
							<NoSearchResults search={emptyStateSearch} marginTop="l" />
						{:else}
							<SearchableBulletedList
								list={libraryListItems}
								searchHighlight={searchQuery.trim()}
								on:linkClick={(e) => insertCode(e.detail.id, e.detail.index.toString())}
							/>
						{/if}
					</div>
				</div>
			{:else}
				<Text styleType="body" class="text-center text-bold mt-l mb-l"
					>{$format('loading', {
						default: 'Loading ...',
					})}</Text
				>
			{/if}
		</svelte:fragment>
	</Modal>
{/if}

<style>
	.library-layout {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		max-height: 100%;
	}

	.library-filter-bar {
		flex-shrink: 0;
		position: sticky;
		top: 0;
		z-index: 1;
		padding-bottom: 12px;
		background: var(--color-base);
		border-bottom: 1px solid var(--color-border, rgba(255, 255, 255, 0.12));
	}

	.library-tag-chips {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
	}

	.library-tag-chip-btn {
		all: unset;
		cursor: pointer;
		display: inline-flex;
	}

	.library-tag-chip-btn:hover {
		opacity: 0.85;
	}

	.library-list {
		flex: 1;
		min-height: 0;
		overflow-y: auto;
		padding-top: 12px;
	}
</style>
